import type {
  FaceOrientation,
  ICubeButtonsController,
  ICubeControlIntegrationApi,
  ICubeController,
  ICubeSceneController
} from './interfaces';
import { DisposablesBasket } from '@/engine/Utilities/DisposablesBasket';
import { ArrowDirection } from './CubeButtonsController';
import * as THREE from 'three';

export class CubeController implements ICubeController {
  private readonly _buttonsController: ICubeButtonsController;

  private readonly _sceneController: ICubeSceneController;

  private readonly _integrationApi: ICubeControlIntegrationApi;

  private readonly _subscriptions = new DisposablesBasket();

  public constructor(
    sceneController: ICubeSceneController,
    buttonsController: ICubeButtonsController,
    integrationApi: ICubeControlIntegrationApi
  ) {
    this._sceneController = sceneController;
    this._buttonsController = buttonsController;
    this._integrationApi = integrationApi;

    this._subscriptions.add(
      this._integrationApi.trackCameraOrientationChanges((quaternion) =>
        this._sceneController.setOrientation(quaternion)
      )
    );
    this._subscriptions.add(this._sceneController.trackCameraOrientationChanges(this._handleButtonsVisibility.bind(this)));

    this._subscriptions.add(
      this._sceneController.trackCubeClicked((orientation) => this._updateOrientation(orientation))
    );
    this._subscriptions.add(this._buttonsController.trackButtonClicked(this._handleArrowClicked.bind(this)));
  }

  public dispose(): void {
    this._buttonsController.dispose();
    this._sceneController.dispose();
    this._subscriptions.dispose();
  }

  public getSceneController(): ICubeSceneController {
    return this._sceneController;
  }

  public getButtonsController(): ICubeButtonsController {
    return this._buttonsController;
  }

  private _updateOrientation(orientation: FaceOrientation): void {
    this._integrationApi.updateParentOrientation(orientation.top, orientation.normal); // normal isn't inverted due setCameraOrientation implementation
  }

  private _handleButtonsVisibility(): void {
    const isButtonsVisible = this._sceneController.isAnatomicalOrientation();
    this._buttonsController.changeButtonsVisibility(isButtonsVisible);
  }

  private _handleArrowClicked(direction: ArrowDirection): void {
    const camera = this._sceneController.getCamera();
    const topVector = new THREE.Vector3().setFromMatrixColumn(camera.matrix, 1);
    const directionVector = camera.getWorldDirection(new THREE.Vector3()).clone();

    switch (direction) {
      case ArrowDirection.Left:
        this._updateOrientation(
          this._sceneController.getFaceOrientation(directionVector.clone().cross(topVector).negate())
        );
        break;
      case ArrowDirection.Right:
        this._updateOrientation(this._sceneController.getFaceOrientation(directionVector.clone().cross(topVector)));
        break;
      case ArrowDirection.Down:
        this._updateOrientation(this._sceneController.getFaceOrientation(topVector.clone().negate()));
        break;
      case ArrowDirection.Up:
        this._updateOrientation(this._sceneController.getFaceOrientation(topVector));
        break;
    }
  }
}
